import {h, Fragment} from "preact"
import { useResponse, AutocompleteElement, Keywords, useCurrencyFormatting } from "@nosto/preact"
import { productImagePlaceholder } from "../helpers"
import SubmitButton from "./elements/SubmitButton"
import PopularSearchQueries from "./PopularSearchQueries"
import PopularSites from "./PopularSites"
import { translatedText } from "./utils/translatedText";
import { useEffect, useState } from "preact/hooks"
import { clearSearchInput } from "./utils/clearSearchInput";
import { extractShopifyHandle } from "./utils/extractShopifyHandle";

export default function Autocomplete() {
    const { products, keywords, loading } = useResponse()
    const { format } = useCurrencyFormatting()

    // if (!products?.hits?.length && !keywords?.hits?.length) {
    //     return
    // }

    const [displayProductsState, setDisplayProductsState] = useState("products") 

    useEffect(() => {
        const wrapper = document.querySelector(".nosto-dropdown-form");
        const target = wrapper?.querySelector(":scope > div");
      
        if (!target) {
          console.warn("❌ [NOSTO] .nosto-dropdown-form > div not found");
          return;
        }
      
        const runMigration = (source) => {

          if (window.Shopify?.market?.reinitialize) {
            window.Shopify.market.reinitialize();
          } else {
            console.warn("❌ [NOSTO] Shopify.market.reinitialize not available");
          }
      
          if (window.Nosto?.migrateToShopifyMarket) {
            window.Nosto.migrateToShopifyMarket({
              productSectionElement: ".nosto-product-info",
              productUrlElement: ".nosto-product-url",
              productHandleAttribute: "x-nosto-handle",
              priceElement: ".nosto-newprice.money",
              listPriceElement: ".nosto-oldprice.money",
            });
          } else {
            console.warn("❌ [NOSTO] migrateToShopifyMarket not available");
          }
        };
      
        // 1. Reinitialize on data
        const handleReinitialize = () => {
          if (products?.hits?.length > 0 && !loading) {
            setDisplayProductsState("products");
            requestAnimationFrame(() => runMigration("Autocomplete (hits update)"));
          } else {
            setDisplayProductsState("empty");
          }
        };
      
        handleReinitialize();
      
        // 2. Reinitialize on visibility change
        const visibilityObserver = new MutationObserver(() => {
          const style = window.getComputedStyle(target);
          const isVisible = style.display !== "none";
      
          if (isVisible) {
            console.log("👁️ [NOSTO] Autocomplete became visible");
            runMigration("Autocomplete (visibility change)");
          }
        });
      
        visibilityObserver.observe(target, {
          attributes: true,
          attributeFilter: ["style", "class"],
        });
      
        return () => {
          visibilityObserver.disconnect();
        };
      }, [loading, JSON.stringify(products?.hits)]);      

    return (
        <div
            class="flex-1 overflow-hidden lg:h-full" 
            data-nosto-element="autocomplete"
            id="autocomplete"
        >
            <div class="w-full">
                <div class="searchGrid lg:mb-12">
                    {keywords?.hits?.length > 0 ? (
                        <div
                            class="flex gap-8 pb-48 pt-40 px-8 lg:py-32 lg:col-span-full lg:px-0 lg:gap-12" 
                        >
                            <div class="w-1/2">
                                <p class="text-13-120--0.5-900 capitalize text-black-55  mb-16 lg:mb-12">
                                    {translatedText("suggestions")}
                                </p>
                                <div class="block text-13-120 mb-12  text-black"
                                id="suggestions">
                                    <Keywords
                                        hits={keywords.hits}
                                        keywordComponent={Keyword} 
                                        elementComponent={Element}
                                        keywordInCategoryComponent={KeywordInCategory}
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 lg:pr-24">
                                <PopularSites />
                            </div>
                            {/* {!products?.hits?.length && <SubmitButton text="See all search results" />} */}
                        </div>
                    ):(
                        <div class="flex gap-8 pb-48 pt-40 px-8 lg:py-32 lg:col-span-full lg:px-0 lg:gap-12">
                        <div class="w-1/2">
                            <PopularSearchQueries />
                        </div>

                        <div class="w-1/2 lg:pr-24">
                            <PopularSites />
                        </div>
                    </div>
                    )
                    }

                    {/* PRODUCTS LIST */}
                    <div
                    id="auto-complete-products-list"
                    class={`relative px-12 overflow-y-auto lg:col-span-full lg:flex lg:flex-col lg:px-0 ${
                        displayProductsState === "products" ? "" : "!hidden"
                    }`}
                    >
                    <p class="text-13-120--0.5-700 capitalize text-black pt-32 pb-24 lg:pb-12 px-8 lg:px-0 lg:pt-112">
                    {translatedText("products")}
                    </p>

                    <div class="flex flex-col lg:flex-row lg:flex-wrap lg:grid lg:grid-cols-4 lg:gap-12">
                        {products?.hits.map(hit => {
                        const secondImage = hit.imageUrl || productImagePlaceholder
                        const urls = hit.alternateImageUrls ?? []

                        const override = hit.customFields.find(f => f.key === "custom-featured_image_override")?.value?.trim().replace(".", "")
                        const isOverrideValid = override && override !== "No override" && !override.endsWith(".mp4")

                        const getImage = (key) => urls.find(url => url.includes(key)) || ""
                        const defaultKey = "_003"

                        const fallback = getImage(defaultKey) || urls[0] || ""
                        const overrideImage = isOverrideValid ? getImage(override) : ""
                        const firstImage = (overrideImage || fallback) !== secondImage ? (overrideImage || fallback) : null
                        return (
                            <div 
                                class="nosto-product-info mb-24 lg:w-full lg:mb-0"
                                x-nosto-handle={extractShopifyHandle(hit?.url)}
                            >
                                <AutocompleteElement 
                                key={hit.productId} 
                                hit={hit}
                                >
                                    <div
                                    data-url={hit.url}
                                    class="nosto-product-url ns-d-flex flex-row ns-clickable lg:flex-col lg:gap-12"
                                    data-nosto-element="product"
                                    >
                                        <div class="bg-productImgBg">
                                            <img
                                                class="ns-h-auto object-cover object-center mr-12 lg:mr-0 lg:w-full mix-blend-multiply"
                                                src={hit.imageUrl}
                                                alt={hit.name}
                                                width="80"
                                            />
                                        </div>
                                        <div class="flex flex-col flex-1 gap-4 text-13-120--0.5-700">
                                            <div class="line-clamp-1">{hit.name?.split(" / ")[0]}</div>
                                            <div aria-label="Price" class="price__sale flex gap-8">
                                                <span class="nosto-newprice money">{format(hit.price)}</span>
                                                {hit.listPrice && hit.listPrice > hit.price && (
                                                    <s class="nosto-oldprice money price-item price-item--regular opacity-[.55] text-13-115--0.5-400">
                                                    {format(hit.listPrice)}
                                                    </s>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </AutocompleteElement>
                            </div>
                        )
                        })}
                    </div>
                    </div>

                    {/* NO RESULTS FALLBACK */}
                    <div
                    class={`relative px-12 overflow-y-auto lg:col-span-full lg:flex lg:flex-col lg:px-0 ${
                        displayProductsState === "empty" ? "" : "!hidden"
                    }`}
                    >
                    <p class="text-13-120-600 py-24 lg:pt-112">{translatedText("noResults")}</p>

                    <div class="flex flex-col lg:flex-row lg:flex-wrap">
                        <div class="nosto_element" id="noresults-nosto-1"></div>
                    </div>
                    
                    </div>

                    {/* <div class="relative shrink-0 px-12 flex items-center py-14 max-lg:border-t border-black-15 lg:col-span-6 lg:row-start-2 lg:py-24 lg:px-0">     
                        <button
                        type="button"
                        class="btn black uppercase whitespace-nowrap"
                        onClick={()=> clearSearchInput()}
                        data-scroll-label={translatedText("clearSearch")} 
                        >
                            <span>
                                {translatedText("clearSearch")}
                            </span>
                        </button>
                    </div> */}
                </div>
            </div>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <span data-nosto-element="keyword">
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement hit={hit} filter={filter} class="ns-clickable ns-py-2 ns-px-4 ns-font-4">
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <span class="ns-clickable">
            {category?.value ? (
                <span>
                    {children}{" "}
                    <span class="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </span>
    )
}